import { useState, useEffect, useCallback, useRef } from 'react'
import { RefreshCw, Play, Timer } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { KpiCard } from './analytics/KpiCard'
import {
  getBankHealth,
  runBankHealthCheck,
  getBankPerformance,
} from '@/src/lib/api/admin-analytics'
import type {
  BankHealthItem,
  BankHealthSnapshot,
  BankHealthStatus,
  BankPerformanceRow,
} from '@/src/types/admin/analytics'

const POLL_MS = 30 * 1000 // refresh the cached snapshot (no bank calls)
const AUTO_CHECK_MS = 5 * 60 * 1000 // live probe interval when auto-check is on (banks rate-limit token requests)
const TRAFFIC_WINDOW_HOURS = 24

const statusConfig: Record<
  BankHealthStatus,
  { label: string; badgeClass: string; dotClass: string }
> = {
  healthy: {
    label: 'ปกติ',
    badgeClass: 'bg-green-100 text-green-700 border-green-200',
    dotClass: 'bg-green-500',
  },
  degraded: {
    label: 'ช้า',
    badgeClass: 'bg-amber-100 text-amber-700 border-amber-200',
    dotClass: 'bg-amber-500',
  },
  down: {
    label: 'ขัดข้อง',
    badgeClass: 'bg-red-100 text-red-700 border-red-200',
    dotClass: 'bg-red-500',
  },
  unconfigured: {
    label: 'ยังไม่ตั้งค่า',
    badgeClass: 'bg-slate-100 text-slate-700 border-slate-200',
    dotClass: 'bg-slate-400',
  },
  not_implemented: {
    label: 'ยังไม่รองรับ',
    badgeClass: 'bg-slate-50 text-slate-500 border-slate-200',
    dotClass: 'bg-slate-300',
  },
  disabled: {
    label: 'ปิดชั่วคราว',
    badgeClass: 'bg-slate-50 text-slate-500 border-slate-200',
    dotClass: 'bg-slate-300',
  },
  unknown: {
    label: 'ยังไม่ได้ตรวจสอบ',
    badgeClass: 'bg-blue-50 text-blue-600 border-blue-200',
    dotClass: 'bg-blue-300',
  },
}

function StatusBadge({ status }: { status: BankHealthStatus }) {
  const cfg = statusConfig[status] ?? statusConfig.unknown
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn('h-2 w-2 rounded-full', cfg.dotClass)} />
      <Badge variant="outline" className={cn('text-xs', cfg.badgeClass)}>
        {cfg.label}
      </Badge>
    </span>
  )
}

function formatTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('th-TH') : '—'
}

function BankHealthCard({
  bank,
  traffic,
  checking,
}: {
  bank: BankHealthItem
  traffic?: BankPerformanceRow
  checking: boolean
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">{bank.bank_code}</CardTitle>
          <p className="text-xs text-muted-foreground">{bank.bank_name}</p>
        </div>
        {checking ? (
          <Skeleton className="h-5 w-20" />
        ) : (
          <StatusBadge status={bank.status} />
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold">
            {bank.latency_ms !== null ? `${bank.latency_ms.toLocaleString()} ms` : '—'}
          </span>
          <span className="text-xs text-muted-foreground">เวลาตอบสนองล่าสุด</span>
        </div>

        <p
          className={cn(
            'text-xs break-words',
            bank.status === 'down' ? 'text-red-600' : 'text-muted-foreground'
          )}
        >
          {bank.message ?? '—'}
          {bank.error_code && bank.status === 'down' ? ` (${bank.error_code})` : ''}
        </p>

        {/* Recent checks (oldest -> newest) */}
        <div className="flex items-center gap-1" aria-label="ประวัติการตรวจสอบล่าสุด">
          {bank.history.length === 0 ? (
            <span className="text-xs text-muted-foreground">ยังไม่มีประวัติ</span>
          ) : (
            bank.history.map((h, i) => (
              <span
                key={`${h.checked_at}-${i}`}
                title={`${statusConfig[h.status]?.label ?? h.status} · ${
                  h.latency_ms ?? '—'
                } ms · ${formatTime(h.checked_at)}`}
                className={cn('h-4 w-1.5 rounded-sm', statusConfig[h.status]?.dotClass)}
              />
            ))
          )}
        </div>

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <dt className="text-muted-foreground">Environment</dt>
          <dd className="text-right">{bank.environment}</dd>
          <dt className="text-muted-foreground">Host</dt>
          <dd className="text-right truncate" title={bank.host ?? ''}>
            {bank.host ?? '—'}
          </dd>
          <dt className="text-muted-foreground">ตรวจสอบล่าสุด</dt>
          <dd className="text-right">{formatTime(bank.checked_at)}</dd>
          <dt className="text-muted-foreground">
            Traffic จริง ({TRAFFIC_WINDOW_HOURS} ชม.)
          </dt>
          <dd className="text-right">
            {traffic
              ? `${traffic.total_requests.toLocaleString()} req · avg ${traffic.avg_response_ms} ms`
              : '—'}
          </dd>
        </dl>
      </CardContent>
    </Card>
  )
}

export default function AdminBankHealth() {
  const permissions: string[] = (() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || 'null')?.permissions ?? []
    } catch {
      return []
    }
  })()
  const canRunCheck = permissions.includes('system:configure')

  const [snapshot, setSnapshot] = useState<BankHealthSnapshot | null>(null)
  const [traffic, setTraffic] = useState<BankPerformanceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState(false)
  const [autoCheck, setAutoCheck] = useState(false)
  const checkingRef = useRef(false)

  // Cheap: reads cached state + passive traffic stats. Never calls bank APIs.
  const loadSnapshot = useCallback(async () => {
    try {
      const [snap, perf] = await Promise.all([
        getBankHealth(),
        getBankPerformance(TRAFFIC_WINDOW_HOURS).catch(() => [] as BankPerformanceRow[]),
      ])
      if (!checkingRef.current) setSnapshot(snap)
      setTraffic(perf)
    } catch {
      toast.error('โหลดสถานะธนาคารไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  // Live probes against bank APIs.
  const runCheck = useCallback(
    async (silent = false) => {
      if (checkingRef.current) return
      checkingRef.current = true
      setChecking(true)
      try {
        const snap = await runBankHealthCheck({ force: true })
        setSnapshot(snap)
        if (!silent) {
          const { down, degraded } = snap.summary
          if (down > 0) toast.error(`พบธนาคารขัดข้อง ${down} ราย`)
          else if (degraded > 0) toast.warning(`พบธนาคารตอบสนองช้า ${degraded} ราย`)
          else toast.success('ตรวจสอบเสร็จสิ้น ทุกธนาคารปกติ')
        }
      } catch (err: any) {
        const status = err?.response?.status
        toast.error(
          status === 403
            ? 'คุณไม่มีสิทธิ์รันการตรวจสอบ (ต้องมีสิทธิ์ system:configure)'
            : 'ตรวจสอบสถานะธนาคารไม่สำเร็จ'
        )
        setAutoCheck(false)
      } finally {
        checkingRef.current = false
        setChecking(false)
      }
    },
    []
  )

  useEffect(() => {
    loadSnapshot()
    const id = setInterval(loadSnapshot, POLL_MS)
    return () => clearInterval(id)
  }, [loadSnapshot])

  useEffect(() => {
    if (!autoCheck || !canRunCheck) return
    runCheck(true)
    const id = setInterval(() => runCheck(true), AUTO_CHECK_MS)
    return () => clearInterval(id)
  }, [autoCheck, canRunCheck, runCheck])

  const summary = snapshot?.summary
  const trafficByBank = new Map(traffic.map((t) => [t.bank_code, t]))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">สถานะ API ธนาคาร</h1>
          <p className="text-muted-foreground">
            ตรวจสอบการเชื่อมต่อ API ของแต่ละธนาคารแบบ real-time
          </p>
        </div>
        <div className="flex items-center gap-3">
          {snapshot?.last_run_at && (
            <span className="text-xs text-muted-foreground">
              ตรวจล่าสุด {formatTime(snapshot.last_run_at)}
            </span>
          )}
          <Button
            variant={autoCheck ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAutoCheck((v) => !v)}
            disabled={!canRunCheck}
            aria-pressed={autoCheck}
            title="ตรวจสอบอัตโนมัติทุก 5 นาที (เรียก API ธนาคารจริง)"
          >
            <Timer className="h-4 w-4 mr-2" />
            ตรวจอัตโนมัติ 5 นาที
          </Button>
          <Button variant="outline" size="sm" onClick={loadSnapshot} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} />
            รีเฟรช
          </Button>
          <Button
            size="sm"
            onClick={() => runCheck(false)}
            disabled={checking || !canRunCheck}
            title={canRunCheck ? undefined : 'ต้องมีสิทธิ์ system:configure'}
          >
            <Play className={cn('h-4 w-4 mr-2', checking && 'animate-pulse')} />
            {checking ? 'กำลังตรวจสอบ...' : 'ตรวจสอบตอนนี้'}
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard title="ปกติ" value={summary ? String(summary.healthy) : '—'} loading={loading && !snapshot} />
        <KpiCard title="ช้า" value={summary ? String(summary.degraded) : '—'} loading={loading && !snapshot} />
        <KpiCard title="ขัดข้อง" value={summary ? String(summary.down) : '—'} loading={loading && !snapshot} />
        <KpiCard
          title="ยังไม่ตั้งค่า / ไม่รองรับ / ปิด"
          value={summary ? String(summary.unconfigured + summary.not_implemented + summary.disabled) : '—'}
          loading={loading && !snapshot}
        />
      </div>

      {/* Bank cards */}
      <div>
        <h2 className="text-lg font-semibold mb-3">ธนาคาร</h2>
        {loading && !snapshot ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-64 w-full" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {snapshot?.banks.map((b) => (
              <BankHealthCard
                key={b.bank_code}
                bank={b}
                traffic={trafficByBank.get(b.bank_code)}
                checking={checking && b.status !== 'disabled' && b.status !== 'not_implemented'}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">รายละเอียดการตรวจสอบ</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ธนาคาร</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead className="text-right">Latency</TableHead>
                <TableHead>วิธีตรวจสอบ</TableHead>
                <TableHead>ข้อความ</TableHead>
                <TableHead>เวลา</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {snapshot?.banks.map((b) => (
                <TableRow key={b.bank_code}>
                  <TableCell className="font-medium">{b.bank_code}</TableCell>
                  <TableCell>
                    <StatusBadge status={b.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    {b.latency_ms !== null ? `${b.latency_ms.toLocaleString()} ms` : '—'}
                  </TableCell>
                  <TableCell className="text-xs">{b.check_type ?? '—'}</TableCell>
                  <TableCell className="text-xs max-w-[360px] break-words">
                    {b.message ?? '—'}
                    {b.error_code ? ` (${b.error_code})` : ''}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    {formatTime(b.checked_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {snapshot && (
            <p className="mt-3 text-xs text-muted-foreground">
              เกณฑ์ "ช้า": ตอบสนองเกิน {snapshot.config.degraded_ms.toLocaleString()} ms · timeout{' '}
              {snapshot.config.timeout_ms.toLocaleString()} ms · ผลการตรวจสอบเก็บในหน่วยความจำของเซิร์ฟเวอร์
              (รีเซ็ตเมื่อรีสตาร์ท)
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
